import type { Patient, Reference } from '@medplum/fhirtypes'
import type { ExtendedResource } from '@seen/core/ExtendedResource'
import { invariant } from '@seen/core/invariant'
import {
  isSubscribeable,
  type Subscribeable,
} from '@seen/core/models/subscribeable'
import { Button, cn, TypographyDetail } from '@seen/ui'
import { type JSX, type ReactNode, useMemo } from 'react'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import { CommentComposer } from '#src/components/CommentComposer.tsx'
import { AvatarCircles } from '#src/components/core/AvatarCircles.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { IconUserPlaceholder } from '#src/components/icons/IconUserPlaceholder.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePractitioners } from '#src/hooks/usePractitioners.ts'

export interface ActivityProps<T extends Subscribeable | ExtendedResource> {
  children: ReactNode
  resource: T
  subject?: Reference<Patient>
  className?: string
}

export function Activity<T extends Subscribeable | ExtendedResource>({
  children,
  resource,
  subject,
  className,
}: ActivityProps<T>): JSX.Element {
  const practitioners = usePractitioners()
  const isReadOnly = useIsReadOnly()
  const _isSubscribeable = isSubscribeable(resource)

  const options = useMemo(
    () =>
      _isSubscribeable
        ? practitioners.map((p) => ({
            value: p.reference.reference,
            label: (
              <PractitionerOption
                practitioner={p.reference}
                isSelected={resource.subscribers?.some(
                  (s) => s.reference === p.reference.reference
                )}
              />
            ),
            keywords: p.searchKeywords,
          }))
        : [],
    [practitioners, resource, _isSubscribeable]
  )

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="flex items-center justify-between gap-1">
        <h5>Activity</h5>
        {_isSubscribeable && !isReadOnly && (
          <div className="-my-2.5 flex items-center">
            <SubscribeButton resource={resource} />
            <ResourceFieldForm
              instance={resource}
              valueFn={(r) => r.subscribers}
              patchFn={(r, subscribers) => {
                r.subscribers = subscribers
              }}
              render={({ field: { value, onChange, ...rest } }) => (
                <Combobox
                  options={options}
                  onSelect={(referenceString) => {
                    if (value?.some((v) => v.reference === referenceString)) {
                      onChange(
                        value?.filter((v) => v.reference !== referenceString)
                      )
                    } else {
                      const practitioner = practitioners.find(
                        (p) => p.reference.reference === referenceString
                      )
                      invariant(practitioner, 'Practitioner not found')
                      onChange([...(value ?? []), practitioner.reference])
                    }
                  }}
                  searchPlaceholder="Change subscribers…"
                >
                  <Button variant="ghost" size="icon" className="p-2" {...rest}>
                    {!!value?.length && (
                      <AvatarCircles
                        avatars={value?.slice(0, 3) ?? []}
                        numInLastCircle={(value?.length ?? 0) - 3}
                      />
                    )}
                    {!value?.length && (
                      <div className="p-0.5">
                        <IconUserPlaceholder />
                      </div>
                    )}
                  </Button>
                </Combobox>
              )}
            />
          </div>
        )}
      </div>
      <div className="-mx-1.5">
        <CommentComposer partOf={[resource.reference]} subject={subject} />
      </div>
      <ActivityList>{children}</ActivityList>
    </div>
  )
}

function SubscribeButton({
  resource,
}: {
  resource: Subscribeable
}): JSX.Element {
  const currentPractitioner = useCurrentPractitioner()
  return (
    <ResourceFieldForm
      instance={resource}
      valueFn={(r) => r.isSubscriber(currentPractitioner.reference)}
      patchFn={(r, isSubscriber) => {
        if (isSubscriber) {
          r.addSubscriber(currentPractitioner.reference)
        } else {
          r.removeSubscriber(currentPractitioner.reference)
        }
      }}
      render={({ field: { value: isSubscriber, onChange, ...rest } }) => (
        <Button
          variant="ghost"
          onClick={() => onChange(!isSubscriber)}
          {...rest}
        >
          <TypographyDetail>
            {isSubscriber ? 'Unsubscribe' : 'Subscribe'}
          </TypographyDetail>
        </Button>
      )}
    />
  )
}
